import { Component, ElementRef, Input, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import * as d3Zoom from 'd3-zoom';
import { points3D } from 'd3-3d';
import { Injectable } from '@angular/core';
import * as d3_3d from 'd3-3d';

@Component({
  selector: 'rap-word-count-chart',
  templateUrl: './word-count-chart.component.html',
  styleUrls: ['./word-count-chart.component.scss']
})
export class WordCountChartComponent {

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  @Input() data: any;
  @Input() queryWord: any;
  private g: any;
  private svg: any;
  private width = 500;
  private height = 300;
  private origin = [200, 150];
  private scale = 2.5;
  private alpha = -Math.PI / 4;
  private beta = Math.PI / 4;
  centerType = 'query';
  defaultData: any;


  ngOnInit() {

    if (this.chartContainer) {
      if (this.data && this.data.neighbors) {
        console.log(this.data)
        this.defaultData = JSON.parse(JSON.stringify(this.data));
        this.defaultData.neighbors = this.defaultData.neighbors.map((nn: any) => ({
          ...nn,
          x: nn.x,  // Adding 10 to x
          y: nn.y,  // Subtracting 10 from y
          z: nn.z  // Adding a random value between 10 and 30 to z
        }));
        this.updateNeighborsIfNeeded();
      }

      console.log(this.defaultData.neighbors)
      this.cleanupChart();
      // this.calculateScale();
      this.initializeChart();
    }

  }

  updateNeighborsIfNeeded(): void {
    let query = this.defaultData.query;
    let neighbors = this.defaultData.neighbors;

    if (this.hasMoreThanTwoIdenticalXY(neighbors)) {
      let center: { x: number; y: number; z: number };

      center = this.calculateAverageCenter(query, neighbors);

      console.log('Center:', center);
      this.scale = 8.0
      // Update neighbors to be slightly around the center
      this.defaultData.neighbors = this.adjustNeighborsAroundCenter(neighbors, {
        x: 0,
        y: 0,
        z: 0
      });
      console.log(this.defaultData.neighbors)
    }
  }

  hasMoreThanTwoIdenticalXY(neighbors: any[]): boolean {
    const xyCount = new Map<string, number>();

    for (const neighbor of neighbors) {
      // Round x and y values
      const key = `${Math.round(neighbor.x)},${Math.round(neighbor.y)}`;
      xyCount.set(key, (xyCount.get(key) || 0) + 1);
    }

    // Check if any (x, y) pair appears more than twice
    return Array.from(xyCount.values()).some(count => count > 2);
  }


  updateCenterType() {
    this.cleanupChart();
    if (this.centerType == 'centroid') {
      this.alpha = -Math.PI / 4;
      this.beta = Math.PI / 4;
    } else {
      this.alpha = -Math.PI / 4;
      this.beta = Math.PI / 4;
    }
    this.renderChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log("ngOnChanges")

  }

  private initializeChart(): void {
    const enableZoom = this.hasMoreThanTwoIdenticalXY(this.defaultData.neighbors);
    console.log(enableZoom)
    // const zoom = d3Zoom.zoom()
    //   .scaleExtent([5, 5]) // Allow zoom between 50% and 500%
    //   .on('zoom', (event) => this.zoomed(event));

    this.svg = d3.select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('class', 'nnsContainer')
      .attr('width', this.width)
      .attr('height', this.height)

      // .attr('transform', `translate(${this.origin[0]}, ${this.origin[1]})`) // Apply initial translation
      .call(
        d3.drag()
          .on('drag', (event) => this.dragged(event)),
      );
    // if (enableZoom) {
    // d3.select(this.svg.node().parentNode).call(zoom); // Apply zoom only if needed
    // }
    this.renderChart();
  }

  private renderChart(): void {
    const scatterData = this.prepareData();
    const projectedPoints = scatterData.map(d => this.project3D(d.x, d.y, d.z));


    const axisLength = 20;  // Define how long each axis should appear
    const axes = [
      { label: 'x', color: '#fbcaca', endPoint: { x: axisLength, y: 0, z: 0 } }, // x-axis
      { label: 'y', color: '#bdf6c3', endPoint: { x: 0, y: axisLength, z: 0 } }, // y-axis
      { label: 'z', color: '#bdeef6', endPoint: { x: 0, y: 0, z: axisLength } }  // z-axis
    ];

    axes.forEach(axis => {
      const projectedStart = this.centerType == 'centroid' ? this.project3D(1, 1, 1) : this.project3D(0, 0, 0); // Project origin
      const projectedEnd = this.project3D(axis.endPoint.x, axis.endPoint.y, axis.endPoint.z); // Project axis end
      console.log(this.scale)
      // Draw line from origin to axis end
      this.svg.append('line')
        .attr('x1', this.origin[0] + projectedStart[0] * this.scale)
        .attr('y1', this.origin[1] - projectedStart[1] * this.scale)
        .attr('x2', this.origin[0] + projectedEnd[0] * this.scale)
        .attr('y2', this.origin[1] - projectedEnd[1] * this.scale)
        .attr('stroke', axis.color)
        .attr('stroke-width', 2);
    });


    this.svg.selectAll("circle")
      .data(projectedPoints)
      .enter()
      .append("circle")
      .attr("cx", d => this.origin[0] + d[0] * this.scale)
      .attr("cy", d => this.origin[1] - d[1] * this.scale)
      .attr("r", 5)
      .style("fill", (d, i) => i === 0 ? '#FF5733' : '#3498DB'); // Different colors for query and neighbors

    this.svg.selectAll("text")
      .data(projectedPoints)
      .enter()
      .append("text")
      .attr("x", d => this.origin[0] + d[0] * this.scale + 5)
      .attr("y", d => this.origin[1] - d[1] * this.scale - 5)
      .text((d, i) => i === 0 ? this.defaultData.query.word : this.defaultData.neighbors[i - 1].word);


    // const xScale3d = d3_3d._3d().x(d => d.x).y(d => d.y).z(d => d.z);
    // const yScale3d = d3_3d._3d().x(d => d.x).y(d => d.y).z(d => d.z);
    // const zScale3d = d3_3d._3d().x(d => d.x).y(d => d.y).z(d => d.z);


    // /* ----------- x-Scale ----------- */
    // let xScale = this.svg.selectAll("path.xScale").data(this.data[1]);

    // xScale
    //   .enter()
    //   .append("path")
    //   .attr("class", "_3d xScale")
    //   .merge(xScale)
    //   .attr("stroke", "red")
    //   .attr("stroke-width", 0.75)
    //   .attr("opacity", 0.5)
    //   .attr("d", xScale3d.draw);

    // xScale.exit().remove();

    // /* ----------- y-Scale ----------- */
    // let yScale = this.svg.selectAll("path.yScale").data(this.data[2]);

    // yScale
    //   .enter()
    //   .append("path")
    //   .attr("class", "_3d yScale")
    //   .merge(yScale)
    //   .attr("stroke", "green")
    //   .attr("stroke-width", 0.75)
    //   .attr("opacity", 0.5)
    //   .attr("d", yScale3d.draw);

    // yScale.exit().remove();

    // /* ----------- z-Scale ----------- */
    // let zScale = this.svg.selectAll("path.zScale").data(this.data[3]);

    // zScale
    //   .enter()
    //   .append("path")
    //   .attr("class", "_3d zScale")
    //   .merge(zScale)
    //   .attr("stroke", "blue")
    //   .attr("stroke-width", 0.75)
    //   .attr("opacity", 0.5)
    //   .attr("d", zScale3d.draw);

    // zScale.exit().remove();

    // this.svg.selectAll("._3d").sort(d3_3d._3d().sort);


    // this.svg.call(
    //   d3.zoom()
    //     .scaleExtent([0.5, 5]) // Define min and max zoom level
    //     .on("zoom", (event) => this.zoomed(event)));

  }



  private prepareData(): { x: number; y: number; z: number; }[] {
    const query = this.defaultData.query;
    console.log(this.defaultData.neighbors)
    const neighbors = this.defaultData.neighbors;
    let center;

    if (this.centerType == 'centroid') {
      // Use query as center if queryWord is provided
      center = { x: query.x, y: query.y, z: query.z };
      console.log(center)

    } else {

      // Calculate center as the average of query and neighbors' coordinates
      center = { x: 0, y: 0, z: 0 };
      // const allPoints = [query, ...neighbors];
      // for (const dim of ["x", "y", "z"]) {
      //   center[dim] = allPoints.reduce((sum, point) => sum + point[dim], 0) / allPoints.length;
      // }

      // for (const dim of ['x', 'y', 'z']) {
      //   let sum = query[dim];
      //   for (const nn of neighbors) {
      //     sum += nn[dim];
      //   }
      //   center[dim] = sum / (neighbors.length + 1);
      // }
      // center = center;
      console.log(center)
    }

    // Return center as the first point and neighbors as the rest
    return [
      { x: center.x, y: center.y, z: center.z },
      ...neighbors.map(nn => ({ x: nn.x, y: nn.y, z: nn.z }))
    ];
  }

  // private project3D(x: number, y: number, z: number): [number, number] {
  //   const cosA = Math.cos(this.alpha);
  //   const sinA = Math.sin(this.alpha);
  //   const cosB = Math.cos(this.beta);
  //   const sinB = Math.sin(this.beta);

  //   const x2d = cosB * (sinA * y + cosA * x) - sinB * z;
  //   const y2d = sinB * (sinA * y + cosA * x) + cosB * z;

  //   return [x2d, y2d];
  // }

  private project3D(x: number, y: number, z: number): [number, number] {
    // const cosA = Math.cos(this.alpha);
    // const sinA = Math.sin(this.alpha);
    // const cosB = Math.cos(this.beta);
    // const sinB = Math.sin(this.beta);

    // const x2d = cosA * x - sinA * z;  // Rotate around y-axis
    // const y2d = sinB * (sinA * x + cosA * z) + cosB * y;  // Rotate around x-axis
    // const z2d = cosB * (cosA * z + sinA * x) - sinB * y;

    const cosA = Math.cos(this.alpha);
    const sinA = Math.sin(this.alpha);
    const cosB = Math.cos(this.beta);
    const sinB = Math.sin(this.beta);

    // Apply rotations
    const xNew = cosA * x - sinA * z;  // Rotate around Y
    const zNew = sinA * x + cosA * z;  // Rotate around Y
    const yNew = cosB * y - sinB * zNew; // Rotate around X

    return [xNew, yNew];

  }


  private dragged(event: any): void {
    this.alpha += event.dx * 0.01;
    this.beta -= event.dy * 0.01;
    this.cleanupChart();
    this.renderChart();
  }

  private cleanupChart(): void {
    if (this.svg) {
      this.svg.selectAll('*').remove();
      this.svg.attr("transform", null);

    }
  }

  private zoomed(event: any): void {
    this.svg.attr("transform", event.transform);
  }

  calculateAverageCenter(query: any, neighbors: any[]): { x: number; y: number; z: number } {
    let totalX = query.x;
    let totalY = query.y;
    let totalZ = query.z;
    let count = 1;

    for (const neighbor of neighbors) {
      totalX += neighbor.x;
      totalY += neighbor.y;
      totalZ += neighbor.z;
      count++;
    }

    return {
      x: totalX / count,
      y: totalY / count,
      z: totalZ / count
    };
  }

  adjustNeighborsAroundCenter(neighbors: any[], center: { x: number; y: number; z: number }): any[] {
    return this.defaultData.neighbors.map((neighbor, index) => ({
      ...neighbor,
      x: center.x + (Math.random() * 2 - 1), // Move closer to center
      y: center.y + Math.random() * 10 - 5,  //Move slightly up
      z: center.z + (Math.random() * 15 - 7),
    }));

  }

}